import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent, DialogOverlay } from '@reach/dialog';
import { VisuallyHidden } from '@reach/visually-hidden';

import styles from './Dialog.module.scss';

interface DialogProps {
  show: boolean;
  ariaLabel: string;
  nodeToFocus?: React.RefObject<HTMLElement | null>;
  onClose?: () => void;
  maxWidth?: `${number}px` | `${number}rem`;
  maxHeight?: `${number}px` | `${number}rem` | `${number}vh`;
  centerOnContent?: boolean;
  /** Show a diplay button allowing to close the dialog */
  showCloseButton?: boolean;
  /** Allow to dismiss the dialog when clicking on the overlay or pressing the escape key */
  allowDismiss?: boolean;
  position?: 'top';
  /** Hid the grey overlay dimming the background */
  hideModalOverlay?: boolean;
}

const Dialog: React.FC<React.PropsWithChildren<DialogProps>> = ({
  children,
  show,
  ariaLabel,
  onClose,
  nodeToFocus,
  maxWidth,
  maxHeight,
  centerOnContent = true,
  showCloseButton = true,
  allowDismiss = true,
  position,
  hideModalOverlay = false,
}) => {
  return (
    <DialogOverlay
      isOpen={show}
      onDismiss={() => {
        if (allowDismiss && onClose) {
          onClose();
        }
      }}
      data-hide-modal-overlay={hideModalOverlay}
      className={styles['dialog-overlay']}
      initialFocusRef={nodeToFocus}
    >
      <DialogContent
        className={styles['dialog-content']}
        data-center-on-content={centerOnContent}
        data-position={position}
        aria-label={ariaLabel}
        style={{
          maxWidth: maxWidth ?? 'fit-content',
          width: maxWidth ? (centerOnContent ? 'calc(94% - var(--nav-offset))' : '94%') : 'auto',
          maxHeight: maxHeight ?? '94%',
          height: maxHeight ? '88%' : 'fit-content',
        }}
      >
        {showCloseButton && onClose ? (
          <button className={styles['close-button']} onClick={onClose}>
            <VisuallyHidden>Close</VisuallyHidden>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        ) : null}
        {children}
      </DialogContent>
    </DialogOverlay>
  );
};

const DialogFooter: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['dialog-footer']}>{children}</div>;
};

export default Dialog;
export { DialogFooter };
